import { Capacitor } from '@capacitor/core';
import { datadogRum } from '@datadog/browser-rum';
import { FIRESTORE_HOST } from 'features/firebase/firebaseConfig';
import { logger } from 'features/logger/logger';
import { DATADOG_BUILD_TAG_KEY, getDatadogVersion } from './datadogVersion';

export const tracedFunctionUrls = [
  {
    match: /^https:\/\/europe-west1-ph-web-eur3-.+\.cloudfunctions\.net\//,
    propagatorTypes: ['tracecontext', 'datadog'],
  },
  {
    match:
      /^https:\/\/us-central1-(ph-web-nam5-.+|cas-ph-uat-webnam5)\.cloudfunctions\.net\//,
    propagatorTypes: ['tracecontext', 'datadog'],
  },
] satisfies Parameters<typeof datadogRum.init>[0]['allowedTracingUrls'];

export const excludedActivityUrls = [
  (url: string) => new URL(url).hostname === FIRESTORE_HOST,
] satisfies Parameters<typeof datadogRum.init>[0]['excludedActivityUrls'];

export const initDatadog = () => {
  if (
    import.meta.env.MODE === 'test' ||
    !import.meta.env.PUBLIC_DATADOG_RUM_APPLICATION_ID ||
    !import.meta.env.PUBLIC_DATADOG_RUM_CLIENT_TOKEN ||
    !import.meta.env.PUBLIC_DATADOG_RUM_ENV ||
    !import.meta.env.PUBLIC_DATADOG_RUM_SERVICE ||
    !import.meta.env.PUBLIC_DATADOG_RUM_SITE
  ) {
    return;
  }

  datadogRum.init({
    applicationId: import.meta.env.PUBLIC_DATADOG_RUM_APPLICATION_ID,
    clientToken: import.meta.env.PUBLIC_DATADOG_RUM_CLIENT_TOKEN,
    site: import.meta.env.PUBLIC_DATADOG_RUM_SITE,
    service: import.meta.env.PUBLIC_DATADOG_RUM_SERVICE,
    env: import.meta.env.PUBLIC_DATADOG_RUM_ENV,
    version: getDatadogVersion(),
    sessionSampleRate: 100,
    sessionReplaySampleRate: 0,
    trackUserInteractions: false,
    trackResources: true,
    trackLongTasks: true,
    defaultPrivacyLevel: 'mask-user-input',
    silentMultipleInit: true,
    sessionPersistence: 'local-storage',
    allowedTracingUrls: tracedFunctionUrls,
    traceSampleRate: 100,
    excludedActivityUrls,
    // Drop Chrome Reporting API events (BeforeUnloadNoGesture, deprecation reports, CSP violations) — browser interventions, not app errors
    beforeSend: event => {
      if (event.type === 'error' && event.error.source === 'report') {
        return false;
      }
      return true;
    },
  });

  datadogRum.setGlobalContextProperty('platform', Capacitor.getPlatform());
  if (import.meta.env.PUBLIC_BUILD_TAG) {
    datadogRum.setGlobalContextProperty(
      DATADOG_BUILD_TAG_KEY,
      import.meta.env.PUBLIC_BUILD_TAG
    );
  }
};

export const datadogRumSetQuestionnaireContext = (questionnaire: {
  documentId: string;
  activityName: string;
  name: string;
}) => {
  logger.debug('Setting questionnaire Datadog RUM context', {
    details: questionnaire,
  });
  datadogRum.setGlobalContextProperty('questionnaire', questionnaire);
};

export const datadogRumRemoveQuestionnaireContext = () => {
  logger.debug('Removing questionnaire Datadog RUM context');
  datadogRum.removeGlobalContextProperty('questionnaire');
};
